/**
 * languages.js — the platform's CANONICAL language registry.
 *
 * One source of truth for: which languages the platform ships, how each one
 * is labelled in a switcher, which direction it sets, and how a session's
 * active language is resolved from the three places a preference can live.
 *
 * Every consumer reads from here — `i18nLoader.js` (which bundles to load),
 * `LangSwitcher.js` (what to list), `setLang.js` (what to accept), the boot
 * sequence (what to start in), the account + workspace settings rows, and
 * the server-side allowlist mirror. Adding a language is ONE edit to
 * `PLATFORM_LANGUAGES` plus its `i18n/<code>/*.json` bundles.
 *
 * ── Frank / FA204 trap (same shape as setLang.js and i18nLoader.js) ────────
 * `functions/` is a frank SECTION BARREL: every export in this directory is
 * emitted as its own entry, and a bare module-scope const read from inside an
 * exported function is unresolvable in the eval-revived runtime. So the codes
 * each function needs are INLINED in that function's own body rather than
 * read off the sibling `PLATFORM_LANGUAGES` export. The array stays exported
 * because cross-package consumers (LangSwitcher, settings rows, landing,
 * docs) import it as a normal ESM binding, which is resolvable.
 */

/** The language every surface falls back to. Nika, 2026-09-02: default English. */
export const DEFAULT_LANG = 'en'

/**
 * Shipped languages, in switcher order.
 *
 * `endonym` is what the switcher shows — a language is named in its own
 * language, never translated into the current one. `english` is for admin
 * surfaces and logs. `dir` is the writing direction the <html> element takes.
 *
 * de / fr / es have `i18n/<lang>/*.json` stubs in this package but every one
 * of them is an empty `{}`. They are NOT listed here: an empty bundle in a
 * switcher is a language that silently renders English, which reads as a
 * broken translation rather than an absent one. Add a code here only when its
 * bundles carry real strings.
 */
export const PLATFORM_LANGUAGES = [
  { code: 'en', endonym: 'English', english: 'English', dir: 'ltr' },
  { code: 'ka', endonym: 'ქართული', english: 'Georgian', dir: 'ltr' }
]

/**
 * Workspace language policies — kept for the server mirror and the admin
 * card, but since 2026-09-10 the two readings RENDER THE SAME: the workspace's
 * own `settings.language` decides its interface language for every member,
 * whether the policy says 'default' or 'enforced'.
 *
 *   Nika, 2026-09-10: "default language is english. only workspaces that are
 *   specifically set as Georgian should render in georgian."
 *
 * Before that, 'default' ranked the workspace language BELOW the member's
 * account preference and this device's stored choice, and 'enforced' ranked
 * it above them. That order let a person's or a device's language paint a
 * workspace that never chose one (measured on dev 2026-09-10: the Symbols HQ
 * workspace rendered Georgian off `user.preferences.lang: 'ka'` +
 * `localStorage.lang: 'ka'`, with `workspace.settings.language` unset). The
 * language is a property of the WORKSPACE — the company's working language —
 * not of the person reading it, so those two levels no longer decide.
 *
 * Mirrored server-side by `core/constants/languages.js` (LANGUAGE_POLICIES),
 * which validates both writers of `settings.languagePolicy`.
 */
export const LANGUAGE_POLICIES = ['default', 'enforced']

/** The policy a workspace that never set one behaves as. */
export const DEFAULT_LANGUAGE_POLICY = 'default'

/**
 * THE one decision: does this workspace's settings bag enforce a language,
 * and if so which one?
 *
 * Returns a shipped language code, or `null` for "no enforcement" — which
 * covers every honest reason there is none: no bag, policy absent or
 * 'default', policy enforced but no language chosen yet, or a language the
 * platform does not ship. An enforced policy pointing at an unshipped code
 * enforces NOTHING rather than stranding every member in a language whose
 * bundles were never built. That is deliberate and it is why the server
 * accepts the half-configured pair instead of refusing it: the admin card
 * writes the two fields as independent requests, and this function is the
 * ONE place that decides whether the pair is live.
 *
 * Accepts the settings bag from either source, because they are the same
 * shape on purpose: a live `workspace.settings` read, or the
 * `workspaceLanguage` sub-bag the public custom-domain scope carries so a
 * branded host can enforce before it has a session.
 *
 * @param {object} [settings] - `{ language, languagePolicy }`
 * @returns {string|null} a shipped language code, or null
 */
export const enforcedWorkspaceLang = (settings) => {
  const SUPPORTED = ['en', 'ka']
  const bag = settings || {}
  if (bag.languagePolicy !== 'enforced') return null
  const code = bag.language
  return typeof code === 'string' && SUPPORTED.indexOf(code) !== -1 ? code : null
}

/** Shipped language codes only — the allowlist every writer validates against. */
export const isSupportedLang = (code) => {
  const SUPPORTED = ['en', 'ka']
  return typeof code === 'string' && SUPPORTED.indexOf(code) !== -1
}

/** Writing direction for a code. Unknown codes read left-to-right. */
export const langDir = (code) => {
  const RTL_LANGS = ['ar', 'he', 'fa', 'ur']
  return RTL_LANGS.indexOf(code) !== -1 ? 'rtl' : 'ltr'
}

/**
 * Resolve the interface language for a session.
 *
 * THE LAW (Nika, 2026-09-10): a workspace renders in the language IT is set
 * to; a workspace that is set to nothing renders in English. A person's
 * account preference and a device's stored choice never decide a workspace's
 * interface language.
 *
 * Precedence, highest first:
 *   0. `workspaceEnforced` — the active workspace's language when its admin set
 *                            `settings.languagePolicy: 'enforced'` (resolve it
 *                            with `enforcedWorkspaceLang`). Same answer as the
 *                            next level today; kept as the top level so an
 *                            enforced pair stays live even if a lower level is
 *                            ever re-introduced.
 *   1. `workspaceDefault`  — `workspace.settings.language`, the admin's choice
 *                            for the workspace. THIS is "specifically set".
 *   2. `brandDefault`      — `branding.lang` on the org behind a white-label
 *                            host, resolved pre-auth from the hostname alone:
 *                            the org itself chose it, and it is the only
 *                            signal a branded login has before sign-in.
 *   3. `DEFAULT_LANG`      — English.
 *
 * `userPreference` (`user.preferences.lang`) and `stored` (`localStorage.lang`)
 * are ACCEPTED AND IGNORED. They were levels 1 and 2 until 2026-09-10; the one
 * place that ranks levels is here, so an old caller stays byte-compatible while
 * the law lives in one function. `setLang` still records a person's explicit
 * switch (account + device) for the standalone surfaces that keep a switcher;
 * the workspace shell reads neither.
 *
 * An unshipped or malformed code at any level is skipped, not honoured, so a
 * stale `workspace.settings.language = 'fr'` cannot strand a session in a
 * language whose bundles were never shipped.
 *
 * @param {object} sources
 * @param {string} [sources.workspaceEnforced]
 * @param {string} [sources.workspaceDefault]
 * @param {string} [sources.brandDefault]
 * @param {string} [sources.userPreference] ignored (see above)
 * @param {string} [sources.stored] ignored (see above)
 * @returns {string} a shipped language code
 */
export const resolveLang = (sources) => {
  const SUPPORTED = ['en', 'ka']
  const src = sources || {}
  const ok = (c) => typeof c === 'string' && SUPPORTED.indexOf(c) !== -1
  if (ok(src.workspaceEnforced)) return src.workspaceEnforced
  if (ok(src.workspaceDefault)) return src.workspaceDefault
  if (ok(src.brandDefault)) return src.brandDefault
  return 'en'
}
